import { FC, useEffect } from 'react';
import { IQueryParamDrivenDrawerContentProvider } from '../../components/query-param-driver-drawer/IQueryParamDrivenDrawerContentProvider';
import { QueryParamDrivenDrawerHelper } from '../../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { useSubscription } from '@meraki-internal/state';
import { DrawerPage } from '../../components/CareMapDrawerPage';
import { BlockUntilActiveCaremap } from '../../ActiveCareMapProvider';
import { createUseStyles } from 'react-jss';
import { ViewNoteMenu } from './ViewNoteMenu';
import { NoteDateTarget } from '../note-list/CareMapNotesListTitle';
import { UserNotes } from './UserNotes';
import { EditNoteWithQueryParamDrawer } from '../edit-note/EditNoteWithQueryParamDrawer';
import { Overview } from './Overview';
import { ActionItems } from './ActionItems';
import { DetailedSummary } from './DetailedSummary';
import { Attachments } from './Attachments';
import { Transcription } from './Transcription';
import { Participants } from './Participants';
import { CareMapNoteTracker } from '../model/CareMapNoteTracker';

const useStyles = createUseStyles({
    stretchHeight: {
        display: 'flex',
        flexDirection: 'column'
    }
});

interface IViewNoteDrawerProps {
    noteId: string;
    trackingFrom?: string;
};

export class ViewNoteWithQueryParamDrawer implements IQueryParamDrivenDrawerContentProvider<IViewNoteDrawerProps> {
    static inject = () => [QueryParamDrivenDrawerHelper];
    constructor(private drawerHelper: QueryParamDrivenDrawerHelper<IViewNoteDrawerProps>){}

    mode = 'view-note';

    Component = ViewNoteDrawerView;

    options = {
        allowBackdropClose: true
    };

    open = (props: IViewNoteDrawerProps) => {
        this.drawerHelper.open({ mode: this.mode, ...props });
    };

    getUrl = (props: IViewNoteDrawerProps) => {
        return this.drawerHelper.getUrl({ mode: this.mode, ...props });
    };
};

export const ViewNoteDrawerContent: FC<IViewNoteDrawerProps> = ({ noteId, trackingFrom }) => {
    const classes = useStyles();

    const tracker = useInstance(CareMapNoteTracker);
    const editNoteDrawer = useInstance(EditNoteWithQueryParamDrawer);

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const model = notesFactory.forActiveCaremap();
    const note = model.getNote(noteId);

    useEffect(() => {
        if (note) {
            tracker.onNoteViewed(note, trackingFrom);
        }
    }, [note]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!note) {
        return;
    }

    const editHandler = () => {
        editNoteDrawer.open({ noteId: note.noteId, trackingFrom });
    };

    return (
        <div data-type="view-note" data-note-id={note.noteId} className={classes.stretchHeight}>

            <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'}}>
                <div style={{fontSize: 20, fontWeight: 'bold'}}>{note.title}</div>
                <ViewNoteMenu model={model} noteId={noteId} trackingFrom={trackingFrom} />
            </div>

            <NoteDateTarget note={note} showNoteTargetHeader />

            <UserNotes
                content={note.html}
                editHandler={editHandler}
            />

            <Overview note={note} />

            <Participants note={note} />

            <ActionItems note={note} />

            <DetailedSummary note={note} />

            <Attachments
                note={note}
                editHandler={editHandler}
            />

            <Transcription note={note} />
        </div>
    );
};

export const ViewNoteDrawerView: FC<IViewNoteDrawerProps> = ({ noteId }) => {
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);
    return (
        <DrawerPage
            title="View Note"
            hasBack={false}
            data-type="view-note-drawer"
            onClose={drawerHelper.close}
        >
            <BlockUntilActiveCaremap>
                <ViewNoteDrawerContent
                    noteId={noteId}
                    trackingFrom={drawerHelper.getDrawerParams().trackingFrom}
                />
            </BlockUntilActiveCaremap>
        </DrawerPage>
    );
};
